import type { ButtonDropdownProps } from '@cloudscape-design/components/button-dropdown';
import type { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import type { VpnGatewayRecord, VpnLinkAction } from '@fleet-cert-console/shared';

// Pure helpers for the VPN link-control UI (VpnEnrollmentPage + VpnGatewayDetail). Type-only
// imports keep this module runnable under the node-environment vitest config.

/** Link column / detail indicator from the gateway-reported fields; null when never reported. The
 *  hold wins over the raw state (spec 7.3: "held down when vpnHeld") — it is what the supervisor
 *  honours, and a hold with the interface still up only happens when a wg-quick down failed. */
export function linkIndicator(
  g: Pick<VpnGatewayRecord, 'vpnLinkState' | 'vpnHeld'>,
): { type: StatusIndicatorProps.Type; label: 'up' | 'down' | 'held down' } | null {
  if (g.vpnHeld) return { type: 'stopped', label: 'held down' };
  if (g.vpnLinkState === 'up') return { type: 'success', label: 'up' };
  if (g.vpnLinkState === 'down') return { type: 'stopped', label: 'down' };
  return null;
}

/** Autostart column: the reported flag, or an em dash when the gateway has never reported it (absent or `null`). */
export function autostartLabel(vpnAutostart: boolean | null | undefined): 'on' | 'off' | '—' {
  if (vpnAutostart == null) return '—';
  return vpnAutostart ? 'on' : 'off';
}

/** Menu label for the autostart toggle. The device default is ON, so an unreported flag offers "off". */
export function autostartToggleLabel(vpnAutostart: boolean | null | undefined): 'Turn autostart off' | 'Turn autostart on' {
  return vpnAutostart === false ? 'Turn autostart on' : 'Turn autostart off';
}

/** The `enabled` value the toggle sends: the inverse of the current (default-on) flag. */
export function autostartToggleTarget(vpnAutostart: boolean | null | undefined): boolean {
  return vpnAutostart === false;
}

/** Actions-menu item ids for the four link actions (prefixed so they never collide with the
 *  enrollment ids `initiate-this` / `re-initiate`). */
export const LINK_ITEM_IDS = {
  up: 'vpn-up',
  down: 'vpn-down',
  autostart: 'vpn-autostart',
  status: 'vpn-status',
} as const;

/** The link items for the Actions dropdown — only on an applied row, in spec order. */
export function linkActionItems(
  g: Pick<VpnGatewayRecord, 'vpnStatus' | 'vpnAutostart'> | null,
): ButtonDropdownProps.Item[] {
  if (g?.vpnStatus !== 'applied') return [];
  return [
    { id: LINK_ITEM_IDS.up, text: 'Bring VPN up' },
    { id: LINK_ITEM_IDS.down, text: 'Take VPN down…' },
    { id: LINK_ITEM_IDS.autostart, text: autostartToggleLabel(g.vpnAutostart) },
    { id: LINK_ITEM_IDS.status, text: 'Refresh from gateway' },
  ];
}

/** Inverse of LINK_ITEM_IDS; null for any other dropdown id. */
export function linkActionOfItem(id: string): VpnLinkAction | null {
  const entry = (Object.entries(LINK_ITEM_IDS) as [VpnLinkAction, string][]).find(([, itemId]) => itemId === id);
  return entry ? entry[0] : null;
}

/** Success flash after any link action (spec 7.3, verbatim). */
export function linkSentFlash(gatewayId: string): string {
  return `Sent to ${gatewayId}; the gateway answers within ~10 s.`;
}

/** Detail-panel handshake age: unreported → em dash, null → never handshaked, else seconds. */
export function handshakeAgoLabel(sec: number | null | undefined): string {
  if (sec === undefined) return '—';
  if (sec === null) return 'never';
  return `${sec} s ago`;
}

/** Render an optional boolean row field without inventing a value for an unreported one. */
export function yesNo(value: boolean | null | undefined, yes: string, no: string): string {
  if (value == null) return '—';
  return value ? yes : no;
}
